import {ApiError} from '@mintdata-wsp/common/controllers/network/Fetcher';
import {CreateEmbedSnippetRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/CreateEmbedSnippetRequest';
import {ModifyEmbedSnippetRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/ModifyEmbedSnippetRequest';
import {EmbedSnippetResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/EmbedSnippetResponse';
import {EmbedSnippetsResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/EmbedSnippetsResponse';
import {API} from '@mintdata-wsp/common/controllers/network/WorkspaceAPI';
import {Snippet} from '@mintdata-wsp/common/state/DataState';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {ErrorSubMessages} from '@mintdata-wsp/common/state/ui/visualDesigner/ToastData';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import bind from 'bind-decorator';
import {action} from 'mobx';

export class SnippetsController
{
    public init():void
    {
        if( controllers.user.isLoggedIn() )
            this.syncAllSnippets();
    }

    public syncAllSnippets():Promise<any>
    {
        return this.getAllSnippets()
            .then(action((allSnippets:Snippet[]) =>
            {
                rootState.data.snippets = allSnippets;
            }))
            .catch(action(() =>
            {
                rootState.data.snippets = [];
            }));
    }

    public getAllSnippets():Promise<Snippet[]>
    {
        return new Promise(action((resolve, reject) =>
        {
            if( rootState.userSession )
            {
                API.snippets.list()
                    .then(action((response:EmbedSnippetsResponse) =>
                    {
                        if( ApiError.isError(response) )
                        {
                            controllers.ui.toasts.showError(response);
                            reject(null);
                        }
                        else
                        {
                            if( !response.snippets )
                                throw {
                                    message: 'Ops!',
                                    subMessage: ErrorSubMessages.Support,
                                };

                            let allSnippets = response.snippets.map((snippet:any) =>
                            {
                                return Snippet.fromResponse(snippet);
                            });

                            resolve(allSnippets);
                        }
                    }));
            }
            else
                reject(null);
        }));
    }

    public createSnippet(snippet:Snippet):Promise<Snippet>
    {
        return new Promise(action((resolve, reject) =>
        {
            if( rootState.userSession )
            {
                const {name, enabled, embedPosition, embedText} = snippet;

                const req = new CreateEmbedSnippetRequest(name.trim(), enabled, embedPosition, embedText);

                API.snippets.create(req)
                    .then(action((response:EmbedSnippetResponse) =>
                    {
                        if( ApiError.isError(response) )
                        {
                            controllers.ui.toasts.showError(response);
                            reject(null);
                        }
                        else
                        {
                            const newSnippet = Snippet.fromResponse(response);
                            rootState.data.snippets.push(newSnippet);

                            resolve(newSnippet);
                        }
                    }));
            }
            else
                reject(null);
        }));
    }

    public updateSnippet(snippet:Snippet, newSnippet:Snippet):Promise<Snippet>
    {
        return new Promise(action((resolve, reject) =>
        {
            if( rootState.userSession )
            {
                const {name, enabled, embedPosition, embedText} = newSnippet;

                const req = new ModifyEmbedSnippetRequest(name.trim(), enabled, embedPosition, embedText);

                const prevSnippet:Snippet = {...snippet};

                // for fast ui reaction
                snippet.name = name;
                snippet.enabled = enabled;
                snippet.embedPosition = embedPosition;
                snippet.embedText = embedText;

                API.snippets.update(snippet.id, req)
                    .then(action((response:EmbedSnippetResponse) =>
                    {
                        if( ApiError.isError(response) )
                        {
                            snippet.name = prevSnippet.name;
                            snippet.enabled = prevSnippet.enabled;
                            snippet.embedPosition = prevSnippet.embedPosition;
                            snippet.embedText = prevSnippet.embedText;

                            controllers.ui.toasts.showError(response);
                            reject(null);
                        }
                        else
                        {
                            // const updatedSnippet = Snippet.fromResponse(response);

                            // snippet.name = updatedSnippet.name;
                            // snippet.enabled = updatedSnippet.enabled;
                            // snippet.embedPosition = updatedSnippet.embedPosition;
                            // snippet.embedText = updatedSnippet.embedText;

                            resolve();
                        }
                    }));
            }
            else
                reject(null);
        }));
    }

    @bind
    public deleteSnippet(snippet:Snippet):Promise<any>
    {
        return new Promise(action((resolve, reject) =>
        {
            if( rootState.userSession )
            {
                API.snippets.delete(snippet.id)
                    .then(action((response:Response) =>
                    {
                        if( ApiError.isError(response) )
                        {
                            controllers.ui.toasts.showError(response);
                            reject(null);
                        }
                        else
                        {
                            const {snippets} = rootState.data;
                            const index = snippets.indexOf(snippet);

                            snippets.splice(index, 1);

                            resolve();
                        }
                    }));
            }
            else
                reject(null);
        }));
    }

    @bind
    public isValidName(name:string, snippetId:number):boolean
    {
        const snippets = rootState.data.snippets;

        if( !snippets ) return true;

        return !snippets.some((snippet:Snippet) =>
        {
            return snippet.name === name && snippet.id !== snippetId;
        });
    }
}
